import { Component, ElementRef, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { removeAmChartLogo } from 'app/core/shared/amchart4/amchart4.module';
import { JsonPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { AxisItemLocation } from '@amcharts/amcharts4/.internal/charts/axes/Axis';

am4core.useTheme(am4themes_animated);

@Component({
    selector: 'expand-modal',
    templateUrl: 'expand-modal.component.html',
    standalone: true,
    imports: [
        NgClass,
        MatDialogModule,
        MatButtonModule,
        NgIf,
        JsonPipe,
        NgTemplateOutlet,
    ],
})
export class ExpandModalDialog {
    currentYear: number = new Date().getFullYear();
    public title: string = '';

    constructor(
        private elementRef: ElementRef,
        public dialogRef: MatDialogRef<ExpandModalDialog>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {}

    ngOnInit(): void {
        this.title = this.data.title;

        if (
            this.data.functionName &&
            typeof this[this.data.functionName] === 'function'
        ) {
            // Call the specified function
            this[this.data.functionName]();
        }
    }

    formatKpiDisplay(title: string, value: number): string {
        if (!title) return '';
        if (title.includes('{baseline}')) {
            return title.replace('{baseline}', value.toString());
        }
        return `${title} (${value}%)`;
    }

    createRadarChart() {
        this.addLabel();

        const myGraphElement = document.getElementById('expandGraph');
        myGraphElement.style.height = '480px';
        myGraphElement.style.width = '50vw';

        /* Create chart instance */
        let chart = am4core.create('expandGraph', am4charts.RadarChart) as any;
        chart.logo.disabled = true;
        chart.height = 500;

        /* Add data */
        const currentYear = new Date().getFullYear();
        const relevantKpis = this.data?.apiData?.data || [];

        const kpiNumbers = Array.from(
            new Set(relevantKpis.map((k) => k.kpiNumber)),
        ).sort((a: any, b: any) => a - b);

        const years = Array.from(
            new Set(relevantKpis.map((k) => Number(k.year))),
        ).sort((a: any, b: any) => a - b);

        chart.data = kpiNumbers.map((num) => {
            const dataObj: any = {
                shortLabel: `KPI ${num}`,
            };

            years.forEach((year) => {
                const kpi = relevantKpis.find(
                    (k: any) => k.kpiNumber === num && Number(k.year) === year,
                );
                if (kpi) {
                    if (num === 4 || num === 5) {
                        dataObj[year.toString()] = kpi.isAchieved ? 100 : 0;
                    } else {
                        dataObj[year.toString()] = kpi.actualPercentage || 0;
                    }
                    dataObj[`${year}_label`] = this.formatKpiDisplay(
                        kpi.kpiTitle || kpi.title,
                        kpi.baselinePercentage,
                    );
                }
            });

            return dataObj;
        });

        /* Create axes */
        let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'shortLabel';
        categoryAxis.renderer.labels.template.fill = am4core.color('#000');
        categoryAxis.renderer.labels.template.fontSize = 12;

        let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.renderer.axisFills.template.fill = chart.colors.getIndex(2);
        valueAxis.renderer.axisFills.template.fillOpacity = 0.05;
        valueAxis.renderer.gridType = 'polygons';
        valueAxis.renderer.labels.template.disabled = true;
        valueAxis.renderer.labels.template.fill = am4core.color('#000');
        valueAxis.renderer.labels.template.fontSize = 12;

        /* Create and configure series */
        years.forEach((year: any) => {
            let series = chart.series.push(new am4charts.RadarSeries());
            series.dataFields.valueY = year.toString();
            series.dataFields.categoryX = 'shortLabel';
            series.name = year.toString();
            series.strokeWidth = 2;
            series.fillOpacity = 0.2;
            series.tooltipText = `{${year}_label}`;

            let bullet = series.bullets.push(new am4charts.CircleBullet());
            bullet.circle.radius = 4;
            bullet.circle.strokeWidth = 2;
            bullet.circle.fill = am4core.color('#fff');

            if (Number(year) === currentYear) {
                series.fill = am4core.color('#93bfeb');
                series.stroke = am4core.color('#93bfeb');
                bullet.circle.stroke = am4core.color('#93bfeb');
            } else {
                series.fill = am4core.color('#d87c34');
                series.stroke = am4core.color('#d87c34');
                bullet.circle.stroke = am4core.color('#d87c34');
            }
        });

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'bottom';
        chart.legend.valign = 'bottom';
        chart.legend.labels.template.fontSize = 12;
        chart.legend.labels.template.fill = am4core.color('#000');
        chart.legend.itemContainers.template.paddingTop = 0;
        chart.legend.itemContainers.template.paddingBottom = 0;
        chart.legend.marginTop = 20;

        chart.cursor = new am4charts.RadarCursor();
        removeAmChartLogo(this.elementRef.nativeElement);
    }

    addLabel() {
        const containerElement = document.getElementById('label');

        // Clear any existing content in the container
        containerElement.innerHTML = '';
        containerElement.innerHTML =
            this.data.viewComponent.children[1].children[1].outerHTML;
    }

    createPieChart() {
        this.addRiskTable();

        const donutCharts = this.data?.apiData?.data?.donutCharts || [];

        // Create Active chart (index 0)
        if (donutCharts[0]) {
            const activeData = donutCharts[0];
            const chart = am4core.create('expandPieLeft', am4charts.PieChart);
            chart.padding(5, 10, 5, 0);

            chart.data = [
                {
                    category: 'Healthy',
                    value: activeData.healthyPercentage,
                    color: am4core.color('#4CAF50'),
                },
                {
                    category: 'Unhealthy',
                    value: activeData.unhealthyPercentage,
                    color: am4core.color('#FF5733'),
                },
            ];

            chart.innerRadius = am4core.percent(50);

            const pieSeries = chart.series.push(new am4charts.PieSeries());
            pieSeries.dataFields.value = 'value';
            pieSeries.dataFields.category = 'category';
            pieSeries.slices.template.stroke = am4core.color('#fff');
            pieSeries.slices.template.strokeWidth = 2;
            pieSeries.slices.template.strokeOpacity = 1;
            pieSeries.slices.template.propertyFields.fill = 'color';
            pieSeries.labels.template.text =
                "{values.value.percent.formatNumber('#.#')}%";
            pieSeries.alignLabels = false;
            pieSeries.labels.template.padding(0, 0, 0, 0);
            pieSeries.ticks.template.disabled = true;

            pieSeries.hiddenState.properties.opacity = 1;
            pieSeries.hiddenState.properties.endAngle = -90;
            pieSeries.hiddenState.properties.startAngle = -90;

            const activeTitle = chart.seriesContainer.createChild(
                am4core.Label,
            );
            activeTitle.text = 'Active';
            activeTitle.fontSize = 13;
            activeTitle.fill = am4core.color('#000');
            activeTitle.horizontalCenter = 'middle';
            activeTitle.verticalCenter = 'middle';
        }

        // Create Idle chart (index 1)
        if (donutCharts[1]) {
            const idleData = donutCharts[1];
            const chart2 = am4core.create('expandPieRight', am4charts.PieChart);
            chart2.padding(5, 10, 5, 0);

            chart2.data = [
                {
                    category: 'Healthy',
                    value: idleData.healthyPercentage,
                    color: am4core.color('#4CAF50'),
                },
                {
                    category: 'Unhealthy',
                    value: idleData.unhealthyPercentage,
                    color: am4core.color('#FF5733'),
                },
            ];

            chart2.innerRadius = am4core.percent(50);

            const pieSeries2 = chart2.series.push(new am4charts.PieSeries());
            pieSeries2.dataFields.value = 'value';
            pieSeries2.dataFields.category = 'category';
            pieSeries2.slices.template.stroke = am4core.color('#fff');
            pieSeries2.slices.template.strokeWidth = 2;
            pieSeries2.slices.template.strokeOpacity = 1;
            pieSeries2.slices.template.propertyFields.fill = 'color';
            pieSeries2.labels.template.text =
                "{values.value.percent.formatNumber('#.#')}%";
            pieSeries2.alignLabels = false;
            pieSeries2.labels.template.padding(0, 0, 0, 0);
            pieSeries2.ticks.template.disabled = true;

            pieSeries2.hiddenState.properties.opacity = 1;
            pieSeries2.hiddenState.properties.endAngle = -90;
            pieSeries2.hiddenState.properties.startAngle = -90;

            const idleText = chart2.seriesContainer.createChild(am4core.Label);
            idleText.text = 'Idle';
            idleText.fontSize = 13;
            idleText.fill = am4core.color('#000');
            idleText.horizontalCenter = 'middle';
            idleText.verticalCenter = 'middle';
        }

        removeAmChartLogo(this.elementRef.nativeElement);
    }

    addRiskTable() {
        const riskRankingElement = document.getElementById('myRiskRanking');

        riskRankingElement.innerHTML = '';
        riskRankingElement.innerHTML =
            this.data.viewComponent.children[1].children[1].outerHTML;

        const stringPercentageElement =
            document.getElementById('myStringPercentage');

        stringPercentageElement.innerHTML = '';
        stringPercentageElement.innerHTML =
            this.data.viewComponent.children[1].children[2].outerHTML;
    }

    updateData() {
        // Display the well maintenance table in the expanded modal
        const tableElement = document.getElementById('expandMaintenanceTable');
        if (tableElement && this.data.apiData?.data?.items) {
            const items = this.data.apiData.data.items;
            let tableHTML = `
        <table class="table text-center" style="width: 100%; font-size: 13px;">
          <thead>
            <tr class="tableHeader-bg-color">
              <th scope="col">Well Type</th>
              <th scope="col">MTD Plan (%)</th>
              <th scope="col">YTD Actual (%)</th>
            </tr>
          </thead>
          <tbody>
      `;

            items.forEach((item: any) => {
                tableHTML += `
          <tr class="greyBgColor">
            <td class="greyBgColor">${item.wellType}</td>
            <td class="greyBgColor">${item.mtdPlanPercentage}%</td>
            <td class="greyBgColor">${item.ytdActualPercentage}%</td>
          </tr>
        `;
            });

            tableHTML += `
          </tbody>
        </table>
      `;

            tableElement.innerHTML = tableHTML;
        }

        // Display the progress bar in the expanded modal
        const progressElement = document.getElementById(
            'expandMaintenanceProgress',
        );
        if (progressElement && this.data.apiData?.data?.summary) {
            const summary = this.data.apiData.data.summary;
            let progressHTML = '<div class="row progress-bar mt-1">';

            summary.forEach((item: any) => {
                const isIdle = item.status === 'Idle String';
                const width = isIdle ? 60 : 40;
                const barClass = isIdle
                    ? 'idle-progress-bar'
                    : 'active-progress-bar';

                progressHTML += `
          <div class="${barClass} text-center" style="width: ${width}%;">
            ${item.status}, ${item.count}
          </div>
        `;
            });

            progressHTML += '</div>';
            progressElement.innerHTML = progressHTML;
        }
    }

    createGroupBarChart() {
        const myGraphElement = document.getElementById('expandStackedChart');
        myGraphElement.style.height = '55vh';
        myGraphElement.style.width = '70vw';

        // Create chart instance
        const chart = am4core.create('expandStackedChart', am4charts.XYChart);
        const monthlyData = this.data?.apiData?.data?.monthlyData || [];
        const currentYear = new Date().getFullYear();

        // Transform the data for the chart
        chart.data = monthlyData.map((item: any) => ({
            date: new Date(currentYear, item.month - 1, 1),
            baseline: item.baselineCount,
            emerging: item.emergingCount,
            general: item.generalCount,
            monthName: item.monthName,
        }));

        // Create axes
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.renderer.labels.template.fill = am4core.color('#000');
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.minGridDistance = 20;
        dateAxis.dateFormats.setKey('month', 'MMM');
        dateAxis.periodChangeDateFormats.setKey('month', 'MMM');
        dateAxis.renderer.labels.template.paddingTop = 40;
        dateAxis.renderer.labels.template.fontWeight = 'bolder';

        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Create series
        const createSeries = (field: string, name: string, color: string) => {
            const series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueY = field;
            series.dataFields.dateX = 'date';
            series.name = name;
            series.columns.template.tooltipText = '{dateX}: [b]{valueY}[/]';
            series.strokeWidth = 2;
            series.stacked = true;
            series.columns.template.strokeWidth = 0;
            series.fill = am4core.color(color);
        };

        createSeries('baseline', 'Baseline', '#008FD3');
        createSeries('emerging', 'Emerging', '#0065A5');
        createSeries('general', 'General', '#0A195F');

        // Create ranges - vertical 'Month' labels removed per user request
        const createRangeGrid = (date: Date) => {
            const range = dateAxis.axisRanges.create();
            range.date = date;
            range.grid.strokeOpacity = 0.2;
            range.tick.disabled = false;
            range.tick.strokeOpacity = 0.3;
            range.tick.length = 60;
        };

        for (let i = 0; i < 12; i++) {
            createRangeGrid(new Date(currentYear, i, 1));
        }
        createRangeGrid(new Date(currentYear + 1, 0, 1));

        chart.legend = new am4charts.Legend();
        chart.legend.labels.template.fill = am4core.color('#000');
        removeAmChartLogo(this.elementRef.nativeElement);
    }

    createProgresBarChart() {
        const myGraphElement = document.getElementById('expandProgressChart');
        if (!myGraphElement) return;

        myGraphElement.style.height = '40vh';
        myGraphElement.style.width = '70vw';

        // Create chart instance
        const chart = am4core.create('expandProgressChart', am4charts.XYChart);

        // Transform API data for the chart
        const activities = this.data?.apiData?.data?.activities || [];
        chart.data = activities.map((activity: any) => ({
            activity: activity.activityName,
            completed: activity.completePercentage,
            notCompleted: activity.notCompletePercentage,
        }));

        // Create axes
        const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = 'activity';
        categoryAxis.renderer.grid.template.opacity = 0;
        categoryAxis.renderer.labels.template.fill = am4core.color('#000');

        const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.min = 0;
        valueAxis.max = 100;
        valueAxis.renderer.grid.template.opacity = 0;
        valueAxis.renderer.ticks.template.strokeOpacity = 0.5;
        valueAxis.renderer.ticks.template.stroke = am4core.color('#495C43');
        valueAxis.renderer.ticks.template.length = 10;
        valueAxis.renderer.line.strokeOpacity = 0.5;
        valueAxis.renderer.baseGrid.disabled = true;
        valueAxis.renderer.minGridDistance = 40;
        valueAxis.renderer.labels.template.fill = am4core.color('#000');

        // Create series
        const createSeries = (field: string, name: string, color: string) => {
            const series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueX = field;
            series.dataFields.categoryY = 'activity';
            series.stacked = true;
            series.name = name;
            series.columns.template.height = am4core.percent(40);
            series.fill = am4core.color(color);
            series.strokeWidth = 2;
            series.stroke = am4core.color('#ffff');

            const labelBullet = series.bullets.push(
                new am4charts.LabelBullet(),
            );
            labelBullet.locationX = 0.5;
            labelBullet.label.text = '{valueX}';
            labelBullet.label.fill = am4core.color('#fff');
        };

        createSeries('completed', 'Complete', '#008FD3');
        createSeries('notCompleted', 'Not Complete', '#273367');

        chart.legend = new am4charts.Legend();
        chart.legend.labels.template.fill = am4core.color('#000');
        removeAmChartLogo(this.elementRef.nativeElement);
    }
}
